import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  RiCloseLine,
  RiLockLine,
  RiEyeLine,
  RiEyeOffLine,
  RiCheckLine,
  RiCheckboxCircleFill,
} from '@remixicon/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useMfaStepUpGate, isMfaCancelled } from '../hooks/MfaStepUpGate';

const PasswordField = ({ label, value, onChange, required }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="relative flex items-center">
        <RiLockLine className="absolute left-3 w-4 h-4 text-gray-400" />
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 text-gray-400 hover:text-gray-600"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <RiEyeOffLine className="w-4 h-4" /> : <RiEyeLine className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const StrengthBar = ({ score }) => {
  const segments = [
    { active: score >= 1, color: 'bg-rose-500' },
    { active: score >= 2, color: 'bg-orange-400' },
    { active: score >= 3, color: 'bg-orange-400' },
    { active: score >= 4, color: 'bg-green-500' },
  ];
  return (
    <div className="flex gap-1 mt-2">
      {segments.map((seg, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full ${seg.active ? seg.color : 'bg-gray-200'}`}
        />
      ))}
    </div>
  );
};

const RuleItem = ({ ok, children }) => (
  <li className="flex items-center gap-2 text-sm">
    <RiCheckboxCircleFill
      className={`w-4 h-4 flex-shrink-0 ${ok ? 'text-green-500' : 'text-gray-300'}`}
    />
    <span className={ok ? 'text-gray-700' : 'text-gray-500'}>{children}</span>
  </li>
);

const ChangePasswordModal = ({ open, onClose, onSubmit }) => {
  const { gate } = useMfaStepUpGate();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setOldPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const rules = useMemo(
    () => ({
      hasUpperLower: /[a-z]/.test(newPwd) && /[A-Z]/.test(newPwd),
      hasNumber: /\d/.test(newPwd),
      minLength: newPwd.length >= 12,
    }),
    [newPwd]
  );

  const score =
    (rules.hasUpperLower ? 1 : 0) +
    (rules.hasNumber ? 1 : 0) +
    (rules.minLength ? 1 : 0) +
    (newPwd.length >= 16 ? 1 : 0);

  const passwordsMatch = newPwd.length > 0 && newPwd === confirmPwd;
  const allRulesMet = rules.hasUpperLower && rules.hasNumber && rules.minLength;
  const canSubmit = oldPwd.length > 0 && allRulesMet && passwordsMatch;

  if (!open) return null;

  const handleSave = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Gate INSIDE the try so an MFA cancel routes through the catch
      // (see isMfaCancelled guard below) instead of leaking an
      // unhandled rejection from this async onClick handler.
      await gate();
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user?.email) throw new Error('No active session');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: oldPwd,
      });
      if (signInError) {
        setError('Current password is incorrect');
        return;
      }

      if (oldPwd === newPwd) {
        setError('New password must be different from the current password');
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPwd,
      });
      if (updateError) throw updateError;

      onSubmit?.({ oldPwd, newPwd });
      onClose();
      toast.success('Password changed successfully', { position: 'bottom-right' });
    } catch (err) {
      if (isMfaCancelled(err)) return; // user closed the step-up modal — silent
      setError(err.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-1">
          <h3 className="text-lg font-semibold text-primary-700">Change password</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-full"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 pt-3 pb-6 space-y-4">
          <PasswordField
            label="Old Password"
            value={oldPwd}
            onChange={setOldPwd}
            required
          />

          <div>
            <PasswordField
              label="New Password"
              value={newPwd}
              onChange={setNewPwd}
              required
            />
            <StrengthBar score={newPwd.length === 0 ? 0 : score} />
            <ul className="mt-3 space-y-1.5">
              <RuleItem ok={rules.hasUpperLower}>At least 1 uppercase and 1 lowercase</RuleItem>
              <RuleItem ok={rules.hasNumber}>At least 1 number</RuleItem>
              <RuleItem ok={rules.minLength}>At least 12 characters</RuleItem>
            </ul>
          </div>

          <div>
            <PasswordField
              label="Confirm Password"
              value={confirmPwd}
              onChange={setConfirmPwd}
              required
            />
            {confirmPwd.length > 0 && (
              <p
                className={`text-xs mt-1.5 ${
                  passwordsMatch ? 'text-gray-500' : 'text-red-500'
                }`}
              >
                {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit || submitting}
            className={`mt-2 w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
              canSubmit && !submitting
                ? 'bg-primary-700 text-white hover:bg-primary-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <RiCheckLine className="w-4 h-4" />
            {submitting ? 'Saving...' : 'Save password'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ChangePasswordModal;
